import React, { useState, useEffect } from 'react';

import { IndexGridNew } from '@rockerbox/styleguide';
import moment from 'moment';
import { Checkbox } from 'semantic-ui-react';

import {
  combineAndSortExportsData,
  filterExportsByUser,
  buildExportRequestDetailsFields,
} from './helpers';
import { track } from '../../../utils/tracking';
import { getReportDeliveries } from '../../api/appServices';
import { getExportRequestsForAdvertiser, getExportRequestStatus } from '../../api/datalakeAdhocExports';
import { useGlobalState } from '../../hooks/global';
import {
  ConversionCell,
  CustomerTypeCell,
  AttributionModelCell,
  LastDeliveredCell,
  EmailRecipientsCell,
  DeliveryWindowCell,
  ReportTypeCell,
  ReportManageRow,
  StatusCell,
} from '../../views/SavedReports/stateless';
import Drawer from '../Drawer';
import { TreeTableLoader } from '../loaders';
import { buildReportFields } from '../SavedReports/helpers';
import ReportDetails from '../SavedReports/ReportDetails';

const CombinedExportsTable = () => {
  // Get global state
  const { user, segments, account } = useGlobalState();

  // Data state
  const [exportsHistory, setExportsHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Combined data state
  const [combinedExports, setCombinedExports] = useState([]);
  const [filteredExports, setFilteredExports] = useState([]);
  const [showOnlyMyExports, setShowOnlyMyExports] = useState(false);

  // Drawer state
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const fetchCombinedExportsData = () => {
    Promise.all([
      getReportDeliveries(),
      getExportRequestsForAdvertiser(),
    ])
      .then(([reportDeliveriesResponse, exportsResponse]) => {
        const deliveries = reportDeliveriesResponse?.data || [];
        const reportDeliveries = deliveries.map(delivery => ({
          ...delivery,
          delivery_status: new Date(delivery.delivery_timestamp) < moment().subtract(2, 'days').toDate() ? 'EXPIRED' : delivery.delivery_status.toUpperCase(),
        }));
        const exports = exportsResponse || [];

        setExportsHistory(exports);

        const sortedData = combineAndSortExportsData(reportDeliveries, exports);
        setCombinedExports(sortedData);
        setFilteredExports(sortedData);
      })
      .catch(err => {
        console.error('Error fetching export data:', err);
        setExportsHistory([]);
        setCombinedExports([]);
        setFilteredExports([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    fetchCombinedExportsData();
    track('data.exports.ad_hoc_exports.view', {
      pixel_source_name: account.pixel_source_name,
    });
  }, []);

  useEffect(() => {
    const inProgressExports = exportsHistory.filter(exportRecord => exportRecord && exportRecord.status === 'IN_PROGRESS');

    if (inProgressExports && inProgressExports.length) {
      const timeoutId = setTimeout(() => {
        inProgressExports.forEach(request => {
          if (!request || !request.id) return;

          getExportRequestStatus(request.id)
            .catch(err => console.error('Error checking export status:', err))
            .finally(() => {
              fetchCombinedExportsData();
            });
        });
      }, 10000);

      return () => clearTimeout(timeoutId);
    }
  }, [exportsHistory]);

  // Track when drawer is opened with item details
  // CURSOR MIGHT HAVE ADDED THIS IDK
  useEffect(() => {
    if (detailsDrawerOpen && selectedItem) {
      track('data.exports.view_details', {
        id: selectedItem.id || 'unknown',
        name: selectedItem.report_name || 'Unnamed Report',
        type: selectedItem._originalData ? 'old' : 'new',
      });
    }
  }, [detailsDrawerOpen, selectedItem]);

  const handleFilterToggle = () => {
    const newShowOnlyMyExports = !showOnlyMyExports;
    setShowOnlyMyExports(newShowOnlyMyExports);

    if (newShowOnlyMyExports) {
      const filtered = filterExportsByUser(combinedExports, user.email);
      setFilteredExports(filtered);
      track('data.exports.filter.my_exports', { enabled: true }); // CURSOR MIGHT HAVE ADDED THIS IDK
    } else {
      setFilteredExports(combinedExports);
      track('data.exports.filter.my_exports', { enabled: false }); // CURSOR MIGHT HAVE ADDED THIS IDK
    }
  };

  const handleOpenDrawer = item => {
    if (!item) return;
    setSelectedItem(item._originalData || item);
    setDetailsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDetailsDrawerOpen(false);
  };

  const allReportCols = [
    { display: 'Report Name', key: 'report_name' },
    { display: 'Status', key: 'delivery_status', as: StatusCell },
    { display: 'Conversion', key: '', as: ConversionCell(segments) },
    { display: 'Customer Type', key: 'customer_type', as: CustomerTypeCell },
    { display: 'Attribution Model', key: 'model_type', as: AttributionModelCell },
    { display: 'Date Range', key: '', as: DeliveryWindowCell },
    { display: 'Last Delivered', key: 'delivery_timestamp', as: LastDeliveredCell },
    { display: 'Report Type', key: 'report_config_id', as: ReportTypeCell },
    { display: 'Email Recipients', key: 'email_recipients', as: EmailRecipientsCell, headerWidth: 2 },
    {
      display: '',
      key: '',
      as: props => ReportManageRow({
        item: props.item,
        col: {
          key: {
            onDetailsButtonClick: handleOpenDrawer,
            openDetailsDrawer: () => {}, // Empty function as it's handled in onDetailsButtonClick
          },
        },
        showDelete: false,
      }),
    },
  ];

  // Get fields for the current item in the drawer
  const getReportFields = () => {
    if (!selectedItem) return [];
    const isOldExport = Boolean(selectedItem.request_body);

    return isOldExport
      ? buildExportRequestDetailsFields(selectedItem, segments)
      : buildReportFields(selectedItem, segments);
  };

  return (
    <>
      {loading ? (
        <TreeTableLoader />
      ) : (
        <IndexGridNew
          title="Exports"
          topRight={(
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Checkbox
                inverted
                color="purple"
                label="Show only my exports"
                className="header-checkbox"
                checked={showOnlyMyExports}
                onChange={handleFilterToggle}
              />
            </div>
          )}
          celled
          data={filteredExports}
          cols={allReportCols}
          fallBackMsg="No Exports Found"
          itemsPerPage={10}
          paginate
        />
      )}

      <Drawer
        openDrawer={detailsDrawerOpen}
        onDrawerClose={handleDrawerClose}
      >
        <ReportDetails
          fields={getReportFields()}
          title={selectedItem && selectedItem.request_body ? 'Export Details' : 'Report Details'}
        />
      </Drawer>
    </>
  );
};

export default CombinedExportsTable;
